import clsx from "clsx";
import { Modal } from "bootstrap";
import { IconText } from "components/IconText";
import { SpinnerBlock } from "components/Spinner";
import { ProBadge } from "components/subscriptions/ProBadge";
import { Fragment, ReactNode, RefObject, useEffect, useRef } from "react";

export const ModalDialog = (props: {
    children: ReactNode;
    title: string;
    id: string;
    confirmBtnText?: string;
    confirmBtnIcon?: string;
    confirmBtnClass?: string;
    confirmBtnType?: "button" | "submit";
    size?: "md" | "lg";
    loading?: boolean;
    successMessage?: ReactNode;
    onConfirm?: () => void;
    onClose?: () => void;
    onOpen?: () => void;
    disabled?: boolean;
    showPro?: boolean;
    secondaryBtnClass?: string;
    secondaryText?: string;
    secondaryIcon?: string;
    onSecondaryConfirm?: () => void;
    centered?: boolean;
    closeBtnRef?: RefObject<HTMLButtonElement>;
    dark?: boolean;
    centerFooter?: boolean;
}) => {
    const {
        children,
        id,
        title,
        confirmBtnText,
        confirmBtnType,
        confirmBtnIcon,
        size = "md",
        confirmBtnClass = "btn-primary",
        loading,
        successMessage,
        onConfirm,
        onClose,
        onOpen,
        disabled,
        showPro,
        secondaryBtnClass,
        secondaryIcon,
        secondaryText,
        onSecondaryConfirm,
        centered,
        closeBtnRef,
        dark,
        centerFooter,
    } = props;

    const modalRef = useRef<HTMLDivElement>(null);

    // stash close event
    const handleClose = useRef<() => void>();

    useEffect(() => {
        handleClose.current = onClose;
    }, [onClose]);

    const handleOpen = useRef<() => void>();

    useEffect(() => {
        handleOpen.current = onOpen;
    }, [onOpen]);

    // close modal on unmount
    useEffect(() => {
        if (modalRef.current) {
            const modal = Modal.getOrCreateInstance(modalRef.current);

            // on close event
            if (handleClose.current) {
                modalRef.current.addEventListener("hidden.bs.modal", () => {
                    if (handleClose.current) {
                        handleClose.current();
                    }
                });
            }

            if (handleOpen.current) {
                modalRef.current.addEventListener("show.bs.modal", () => {
                    if (handleOpen.current) {
                        handleOpen.current();
                    }
                });
            }

            return () => {
                modal.hide();
            };
        }

        return () => null;
    }, [handleClose]);

    return (
        <div className="modal fade" id={id} ref={modalRef}>
            <div className={clsx("modal-dialog", size === "lg" && "modal-xl", centered && "modal-dialog-centered")}>
                <div className="modal-content">
                    {successMessage ? (
                        <div className="bg-success p-3 d-flex  text-white gap-3  align-items-center">
                            <span className="fas fa-check fs-1"></span>
                            <div className="h4">{successMessage}</div>
                            <div className="ms-auto">
                                <button type="button" className="btn-close" data-bs-dismiss="modal"></button>
                            </div>
                        </div>
                    ) : (
                        <Fragment>
                            <div className={clsx("modal-header bg-dark text-white", dark && "border-0")}>
                                <h2 className="h4">{title}</h2>
                                <button
                                    type="button"
                                    className="btn-close"
                                    data-bs-dismiss="modal"
                                    ref={closeBtnRef}
                                ></button>
                            </div>

                            <div className={clsx("modal-body", dark && "bg-dark")}>{children}</div>

                            {confirmBtnText && (
                                <div
                                    className={clsx(
                                        "modal-footer d-flex gap-2 align-items-center",
                                        centerFooter ? "justify-content-center" : "justify-content-start",
                                        dark && "bg-dark border-0",
                                    )}
                                >
                                    <button
                                        type={confirmBtnType}
                                        disabled={loading || disabled}
                                        className={clsx("btn icon-link", confirmBtnClass)}
                                        onClick={() => {
                                            if (onConfirm) {
                                                onConfirm();
                                            }
                                        }}
                                    >
                                        {confirmBtnIcon && <span className={confirmBtnIcon}></span>}
                                        <span>{confirmBtnText}</span>
                                    </button>

                                    {secondaryBtnClass && secondaryIcon && secondaryText && onSecondaryConfirm && (
                                        <button
                                            type="button"
                                            className={secondaryBtnClass}
                                            onClick={onSecondaryConfirm}
                                            disabled={loading || disabled}
                                        >
                                            <IconText icon={secondaryIcon} text={secondaryText} />
                                        </button>
                                    )}

                                    {loading && <SpinnerBlock className="ms-auto" />}

                                    {showPro && <ProBadge />}
                                </div>
                            )}
                        </Fragment>
                    )}
                </div>
            </div>
        </div>
    );
};
